import {
    Directive,
    ElementRef,
    forwardRef,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewContainerRef,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import {
    bindAutoClose,
    bindFocusTrap,
    bindPosition,
    Strategy,
} from '@cmps/cdk';
import { CmpsComponentPortal, CmpsComponentPortalHandle } from '@cmps/ng-uikit';
import { isValid } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { DateTimeParserFormatter } from './datetime-parser-formatter';
import { DateTimePickerComponent } from './datetime-picker.component';
import { DateTimeProviders } from './datetime-providers';
import { validate } from './datetime-validator';

@Directive({
    selector: 'input[cmpsDateTimePicker]',
    host: {
        '(input)': '_onInput($event.target.value, false)',
        '(change)': '_onInput($event.target.value, true)',
        '(blur)': '_onBlur()',
        '[disabled]': '_isDisabled',
        '[ngModelOptions]': "{ updateOn: 'blur' }",
        '(keydown.enter)': '$event.stopPropagation(); $event.target.blur()',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateTimePickerInput),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DateTimePickerInput),
            multi: true,
        },
        DateTimeProviders,
    ],
    exportAs: 'cmpsDateTimePicker',
})
export class DateTimePickerInput
    implements OnChanges, OnDestroy, ControlValueAccessor, Validator
{
    /** show only the calendar control */
    @Input() onlyDate: boolean;

    /** show only the time control */
    @Input() onlyTime: boolean;

    /** whether to auto focus into the popup after it has opened */
    @Input() autoFocus: boolean;

    /** min date for navigation */
    @Input() minDate: Date;

    /** max date for navigation */
    @Input() maxDate: Date;

    private _onChange: (_: Date) => void;
    private _onChangeInternal: (_: Date) => void;
    private _onTouched: () => void;
    /** @private */ _isDisabled: boolean;
    private _inputValue: string;
    private _value: Date;
    private _onValidatorChange: () => void;

    private _isOpen: boolean;
    private _hdl: CmpsComponentPortalHandle<DateTimePickerComponent>;
    private _cancellationSubject: Subject<void>;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef<HTMLInputElement>,
        private readonly _viewContainerRef: ViewContainerRef,
        private readonly _portal: CmpsComponentPortal,
        private readonly _parserFormatter: DateTimeParserFormatter,
    ) {
        this.autoFocus = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('minDate') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('maxDate')
        ) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._onValidatorChange && this._onValidatorChange();
        }

        if (!this._isOpen || !this._hdl) {
            return;
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('onlyDate')) {
            this._hdl.ref.instance.onlyDate = this.onlyDate;
        }
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('onlyTime')) {
            this._hdl.ref.instance.onlyTime = this.onlyTime;
        }
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('minDate')) {
            this._hdl.ref.instance.minDate = this.minDate;
        }
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('maxDate')) {
            this._hdl.ref.instance.maxDate = this.maxDate;
        }
    }

    ngOnDestroy(): void {
        this.close();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
        this._onChangeInternal = (val) => {
            this._value = val;
            this._updateView();
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            fn && fn(val);

            this._patchTimeComponent();

            if (this.onlyDate) {
                this.close();
            }
        };
        if (this._isOpen && this._hdl) {
            this._hdl.ref.instance.registerOnChange(this._onChangeInternal);
        }
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
        if (this._isOpen && this._hdl) {
            this._hdl.ref.instance.registerOnTouched(fn);
        }
    }

    setDisabledState(isDisabled: boolean): void {
        this._isDisabled = isDisabled;
    }

    writeValue(obj: Date): void {
        this._value = obj;
        this._updateView();
        this._writeValue();
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const { value } = control;
        return validate(value, this.minDate, this.maxDate);
    }

    isOpen(): boolean {
        return this._isOpen;
    }

    toggle(): void {
        this._close();
        if (!this._isOpen) {
            this._open();
        }
        this._isOpen = !this._isOpen;
    }

    open(): void {
        if (!this._isOpen) {
            this.toggle();
        }
    }

    close(): void {
        if (this._isOpen) {
            this.toggle();
        }
    }

    /** @private */
    _onInput(value: string, updateView: boolean): void {
        const changed = value !== this._inputValue;
        if (changed) {
            this._inputValue = value;
            this._value = this._parserFormatter.parse(value, {
                onlyDate: this.onlyDate,
                onlyTime: this.onlyTime,
            });
        }
        if (changed || !updateView) {
            this._writeValue();
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._onChange && this._onChange(this._value);
        }
        if (updateView) {
            this._updateView();
            this._writeValue();
        }
    }

    /** @private */
    _onBlur(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._onTouched && this._onTouched();
    }

    private _updateView(): void {
        const ret = this._parserFormatter.format(this._value, {
            onlyDate: this.onlyDate,
            onlyTime: this.onlyTime,
        });
        if (ret) {
            this._inputValue = ret;
            this._el.nativeElement.value = this._inputValue;
        }
    }

    private _writeValue(): void {
        if (this._isOpen && this._hdl) {
            this._hdl.ref.instance.writeValue(this._value);
            this._hdl.ref.instance.focus();
        }
        this._patchTimeComponent();
    }

    private _open(): void {
        this._hdl = this._portal.create(DateTimePickerComponent);
        this._hdl.ref.changeDetectorRef.detectChanges();
        this._hdl.wrapper.classList.add('cmps-datetime-picker--portal');

        this._hdl.ref.instance.onlyDate = this.onlyDate;
        this._hdl.ref.instance.onlyTime = this.onlyTime;
        this._hdl.ref.instance.minDate = this.minDate;
        this._hdl.ref.instance.maxDate = this.maxDate;
        this._hdl.ref.instance.registerOnChange(this._onChangeInternal);
        this._hdl.ref.instance.registerOnTouched(this._onTouched);
        this._hdl.ref.instance.setDisabledState(this._isDisabled);
        this._hdl.ref.instance.writeValue(this._value);
        this._hdl.ref.instance.focus();
        this._patchTimeComponent();

        this._cancellationSubject = new Subject();

        /// //
        const posFn = bindPosition(
            Strategy.ADJACENT,
            this._hdl.wrapper,
            this._el.nativeElement,
            'bottom-left',
            (pos) => {
                Object.keys(pos.style).forEach((k) => {
                    this._hdl.wrapper.style[k] = pos.style[k];
                });
            },
            this._cancellationSubject,
        );
        this._ngZone.onStable
            .pipe(
                tap(() => {
                    posFn();
                }),
                takeUntil(this._cancellationSubject),
            )
            .subscribe();
        /// //

        bindAutoClose(
            'click|escape',
            this._hdl.wrapper,
            [this._el.nativeElement, this._hdl.wrapper],
            () => this.close(),
            this._cancellationSubject,
        );
        bindFocusTrap(
            this._hdl.wrapper,
            this.autoFocus,
            this._cancellationSubject,
        );
    }

    private _close(): void {
        if (this._cancellationSubject) {
            this._cancellationSubject.next();
            this._cancellationSubject.complete();
        }
        this._cancellationSubject = null;

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._hdl && this._hdl.destroy();
        this._hdl = null;
    }

    /** the time component is too old and doesn't use locale for meridian display. TODO revise */
    private _patchTimeComponent(): void {
        if (!this._hdl || !this._inputValue || !isValid(this._value)) {
            return;
        }
        const el = this._hdl.ref.location.nativeElement.querySelector(
            '.ngb-tp-meridian button',
        );
        if (el) {
            const parts = this._inputValue.split(' ').reverse();
            el.textContent = parts ? parts[0] : el.textContent;
        }
    }
}
